/**
 * THE SCOPE MODEL'S WRITE CONTRACT — the org-wide job-type library, and the four
 * things one Role states on top of it.
 *
 * `24e4adcc5b` landed five tables — `RoleJobType`, `RoleScopeLine`,
 * `RoleVariable`, `RoleWorkingYear`, `RoleSystemPolicy` — and nothing that could
 * put a byte in any of them. This file is the first half of the door: the shapes
 * a caller sends and the shapes it reads back.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE UI IS THE AUTHORITY ON THESE SHAPES, NOT THE SCHEMA COMMENTS.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Two of the field shapes below contradict `schema.prisma`'s own prose, and in
 * both cases the prose describes a design the screen never implemented. A
 * contract written from the comment would typecheck perfectly against a client
 * that could never send it.
 *
 *   1. A part's rate is `source: { kind: "variable" | "fixed" }`. The database
 *      comment says `default? | variableRef?`; the string `variableRef` occurs
 *      ZERO times in the Scope UI. See `role-job-type.schema.ts`.
 *
 *   2. A job type's cost, hours and revenue overrides are INFIX EXPRESSION
 *      STRINGS — `"wage*(1+charges/100)"` — evaluated by a shunting-yard parser
 *      over `+ - * / ( )` and the Role's variables. The database comment calls
 *      them "JSON expression trees in the same shape RoleWorkload and
 *      RoleSystemImpact already use". They are not, and nothing in the product
 *      converts between the two. They are stored as JSON strings in those
 *      `Json?` columns, which is what makes the column type still honest.
 *
 * They are therefore named `costExpression` / `hoursExpression` /
 * `revenueExpression` here and NOT `*Formula`. In this codebase `formula`
 * already means a `CoverageFormula` tree, and reusing the word for an infix
 * string is how the next reader writes a parser for the wrong grammar.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * EVERY OPTIONAL FIELD IS REQUIRED-AND-NULLABLE. NOTHING HERE IS `.optional()`.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The same argument `role-coverage-authoring-input.schema.ts` makes, unchanged:
 * these routes are `PUT`s and `POST`s over whole rows, an absent key would have
 * to mean either "leave it" or "clear it", and both readings are wrong to guess
 * at. A caller clearing a job type's note sends `"note": null` and means it.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * AN UNSET NUMBER IS ABSENT. IT IS NEVER ZERO.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `RoleVariable.value` and all four `RoleWorkingYear` terms are nullable, and
 * `null` is "nobody has filled this in" — a state the Assumptions screen renders
 * as a placeholder. A `.default(0)` anywhere below would erase that distinction
 * on every write and price an unfinished assumption as free, silently, with
 * nobody told. There is deliberately no default on any numeric field in this
 * file.
 */
import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { RoleJobTypePartKeySchema, RoleJobTypePartsSchema } from "./role-job-type.schema";

/*
 * ══════════════════════════════════════════════════════════════════════════
 * `z.enum(DbEnum.X_VALUES)`, NEVER `z.nativeEnum(DbEnum.X)`. THEY INFER
 * DIFFERENT TYPES AND ONLY ONE OF THEM SURVIVES A DATABASE ROW.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `z.nativeEnum(DbEnum.RoleJobTypeBasis)` infers the TypeScript ENUM type.
 * Prisma hands a read row the string-literal UNION (`"SALARY" | "HOURLY" | …`),
 * and a bare string is NOT assignable to a TS enum — so a mapper turning a row
 * into this contract's shape fails with `Type '"CUSTOM"' is not assignable to
 * type 'RoleJobTypeBasis'`, at the mapper, naming the enum rather than the
 * choice made here.
 *
 * `z.enum` over the generated `_VALUES` tuple infers exactly that union, so it
 * round-trips: a row maps in, a request maps out, and Prisma accepts it back.
 *
 * ⚠️ THE WRONG SPELLING IS ALL OVER THIS PACKAGE AND IS NOT A BUG THERE.
 * `conversation-filters.schema.ts` and friends use `z.nativeEnum` correctly,
 * because those enums only ever arrive on a REQUEST and are never assigned FROM
 * a row. The rule is not "nativeEnum is wrong" — it is "a schema that a row has
 * to satisfy needs the union". Copying a filter schema into a read contract is
 * how this gets rediscovered.
 */

/**
 * An infix expression a job type overrides its basis with.
 *
 * Free text on purpose: the grammar is the client's shunting-yard parser, the
 * variable names it may reference are the Role's own `RoleVariable` rows, and
 * those are resolved per Role at evaluation time. A server-side parse here
 * would need the Role's variables — which the org-scoped job-type library does
 * not have and must not have, because the same type resolves `wage` to a
 * different number in every Role that uses it.
 *
 * `null` is "this type does not override its basis", which is the normal state.
 * An EMPTY STRING is not the same thing and is legal: the evaluator reads it as
 * zero, which is what a `credit` type with no cost wants.
 */
const RoleJobTypeExpressionSchema = z.string().max(500).nullable();

/**
 * One job type, as a caller AUTHORS it.
 *
 * `position` is absent on purpose — it is assigned by the server on create and
 * preserved on update. A client that could set it would be able to collide two
 * types on one slot, and the library's read order already breaks a tie on
 * `createdAt`.
 *
 * The length bounds restate the database's CHECK constraints
 * (`RoleJobType_name_shape` and friends) rather than leaving them to Postgres.
 * A constraint violation arrives as a `P2010` and renders as a 500, telling the
 * caller the server broke when their input was invalid.
 */
export const RoleJobTypeInputSchema = z.strictObject({
  /** What the arrangement is called on screen: "Support agent, Manila". */
  name: z.string().trim().min(1).max(160),
  /** How the arithmetic works. Fixes the default cost and hours expressions. */
  basis: z.enum(DbEnum.ROLE_JOB_TYPE_BASIS_VALUES),
  /** Which of the four Scope-tab headings it subtotals under. */
  group: z.enum(DbEnum.ROLE_JOB_TYPE_GROUP_VALUES),
  /** The listing band inside the "Add a line" picker. Display, not arithmetic. */
  category: z.string().trim().min(1).max(80),
  /** What ONE unit of this type is: "people", "seats", "h / wk", "tickets / wk". */
  quantityUnit: z.string().trim().min(1).max(40),
  /** The author's own sentence about the type. `null` when they wrote none. */
  note: z.string().max(2000).nullable(),
  /**
   * The fraction of a full contract a SALARY line buys — `0.6` for a 60%
   * part-time contract. It divides the hours and never the cost, because the
   * cost is already stated as that contract's own gross.
   *
   * `null` is a full contract. Not `1`: the two render identically today, and
   * only `null` survives a later change to what "full" means.
   */
  fte: z
    .number()
    .refine((n) => Number.isFinite(n) && n > 0 && n <= 1, {
      message: "fte must be finite, greater than 0 and at most 1"
    })
    .nullable(),
  /** The rate inputs. See `role-job-type.schema.ts` — this is the whole model. */
  parts: RoleJobTypePartsSchema,
  /** Overrides the basis' cost-per-unit expression. `null` to use the basis'. */
  costExpression: RoleJobTypeExpressionSchema,
  /** Overrides the basis' hours-per-unit expression. `null` to use the basis'. */
  hoursExpression: RoleJobTypeExpressionSchema,
  /** Revenue one unit credits back a year. `null` when unmodelled. */
  revenueExpression: RoleJobTypeExpressionSchema
});

/** One job type, as the API returns it. */
export const RoleJobTypeViewSchema = RoleJobTypeInputSchema.extend({
  id: z.string(),
  /** The library's own order. Assigned by the server, never by a caller. */
  position: z.number(),
  updatedAt: z.string()
});

/**
 * The organisation's job-type library, and the rows it could not read.
 *
 * 🚨 `unreadable` IS NOT DECORATION AND MUST NOT BE DROPPED. `parts` is a `Json`
 * column, so a row whose parts do not satisfy `RoleJobTypePartsSchema` has three
 * possible answers and two of them are lies: falling back to `[]` reports a type
 * with no rates, which prices every line that uses it at ZERO with nothing
 * anywhere saying so, and throwing turns one bad row into a failed listing for
 * the whole organisation. So the row is withheld from `jobTypes` and its id is
 * named here, which is the only answer a client can act on.
 *
 * Every write in this contract parses `parts` before it reaches the column and
 * the column carries a `jsonb_typeof = 'array'` CHECK, so a non-empty
 * `unreadable` means a hand-edited row or a contract that has since narrowed —
 * both worth seeing rather than absorbing.
 */
export const RoleJobTypeLibraryViewSchema = z.strictObject({
  jobTypes: z.array(RoleJobTypeViewSchema),
  /** Ids of rows whose stored `parts` did not parse. Normally empty. */
  unreadable: z.array(z.string())
});

/**
 * One line of a Role's Scope, as a caller authors it.
 *
 * NO `id` AND NO `position`. The whole list is replaced in one request, so a
 * line's identity is its index in the array — see `RoleScopeLinesInputSchema`.
 *
 * `quantity` IS A NUMBER HERE and a string in the editor. The editor holds what
 * the person typed so a decimal point survives being pressed; converting at the
 * boundary is the client's job, and a value that will not convert is a form
 * error rather than a request.
 */
export const RoleScopeLineInputSchema = z.strictObject({
  /** A row of the organisation's job-type library. */
  jobTypeId: z.string().uuid(),
  /**
   * How many units of that type. `>= 0` and finite, restating
   * `RoleScopeLine_quantity_finite`.
   *
   * Zero is legal and is not the same as deleting the line: a line stated at
   * zero is a decision recorded, and the row keeps its `scope` sentence.
   */
  quantity: z.number().refine((n) => Number.isFinite(n) && n >= 0, {
    message: "quantity must be finite and at least 0"
  }),
  /**
   * Where the line applies, in the author's own words — "France · Monday to
   * Friday", "November to January".
   *
   * Free text, meaningless to the engine, and an EMPTY STRING IS LEGAL: it is
   * the author having cleared the field, not a missing value.
   */
  scope: z.string().max(200)
});

/**
 * A Role's whole Scope, in order.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REPLACE-THE-LIST, NOT UPSERT-ON-A-UNIQUE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `RoleScopeLine` has NO unique constraint on `(roleId, jobTypeId)` and adding
 * one would make an ordinary case unrepresentable: six seats in Manila and four
 * in Lisbon are TWO lines of one job type with different `scope` text. So there
 * is no key to upsert on, and per-line CRUD would need the client to send a
 * `position` on every line and keep the set consistent across three verbs.
 *
 * The array's INDEX is the position. One request states the whole list, one
 * transaction replaces it, and a reorder is the same request with the elements
 * moved — no `.../order` literal route, which is also what keeps this resource
 * out of the `@Get(":id")` catch-all race that has already killed two Roles
 * endpoints.
 *
 * ⚠️ ROW IDS DO NOT SURVIVE A SAVE. The replace deletes and re-inserts, so every
 * line comes back with a new `id`. Nothing references a scope line by id, and
 * the editor holds its own keys until it saves — but do not build anything that
 * assumes otherwise.
 */
export const RoleScopeLinesInputSchema = z.strictObject({
  lines: z.array(RoleScopeLineInputSchema).max(200)
});

/** One stored line of a Role's Scope, as the API returns it. */
export const RoleScopeLineViewSchema = RoleScopeLineInputSchema.extend({
  id: z.string(),
  /** Its place in the list, `0`-based. The read is ordered by it. */
  position: z.number(),
  updatedAt: z.string()
});

/** A Role's whole Scope, in read order. */
export const RoleScopeLinesViewSchema = z.strictObject({
  lines: z.array(RoleScopeLineViewSchema)
});

/**
 * One shared assumption of a Role, as a caller authors it.
 *
 * `key` is what a job type's `parts[].source.variable` matches, so it carries
 * the same shape as a part key — one rule for one concept, because two rules
 * are how a key that works in one place is refused in the other.
 */
export const RoleVariableInputSchema = z.strictObject({
  key: RoleJobTypePartKeySchema,
  /** What it is called on the Assumptions tab. */
  label: z.string().trim().min(1).max(160),
  /** The author's note about what it means. `null` when they wrote none. */
  description: z.string().max(2000).nullable(),
  /** How it reads — "%", "€ / h", "weeks". Display only, never parsed. */
  unit: z.string().max(80).nullable(),
  /**
   * 🚨 `null` IS "NOBODY HAS FILLED THIS IN" AND IS NOT ZERO.
   *
   * The Assumptions screen renders an unset variable as a placeholder and a
   * stated `0` as a decision somebody made. Collapsing the two prices an
   * unfinished assumption at nothing, on a screen whose whole purpose is that
   * one edit re-prices every line that reads it.
   */
  value: z.number().nullable()
});

/**
 * Every shared assumption of one Role, in order.
 *
 * Replace-the-list for the same reason as the Scope: the Assumptions tab is one
 * form holding the whole set, `position` is the array index, and a rename of a
 * key is a delete plus an insert however it is spelled.
 *
 * 🚨 KEYS ARE UNIQUE WITHIN A ROLE, matching `@@unique([roleId, key])`. Two rows
 * named `wage` make every `parts[].source.variable: "wage"` ambiguous and the
 * resolver would pick one by query order. Refused here by name and by index so
 * the editor can highlight the offending field rather than reject the form.
 */
export const RoleVariablesInputSchema = z.strictObject({
  variables: z
    .array(RoleVariableInputSchema)
    .max(128)
    .superRefine((variables, ctx) => {
      const seen = new Set<string>();
      variables.forEach((variable, index) => {
        if (seen.has(variable.key)) {
          ctx.addIssue({
            code: "custom",
            path: [index, "key"],
            message: `duplicate variable key "${variable.key}" — a part referencing it would be ambiguous`
          });
        }
        seen.add(variable.key);
      });
    })
});

/** One stored assumption, as the API returns it. */
export const RoleVariableViewSchema = RoleVariableInputSchema.extend({
  id: z.string(),
  position: z.number(),
  updatedAt: z.string()
});

/** Every stored assumption of one Role, in read order. */
export const RoleVariablesViewSchema = z.strictObject({
  variables: z.array(RoleVariableViewSchema)
});

/**
 * One term of a working year: a finite number, or `null` for "not stated".
 *
 * The bounds restate the database's four CHECKs. They are NOT checked against
 * each other, and that is deliberate at both layers: leave plus holidays plus
 * sickness can exceed the calendar year while somebody is halfway through
 * typing, and a rule that fires on a half-finished form is a rule somebody
 * removes.
 */
const workingYearTerm = (max: number, unit: string) =>
  z
    .number()
    .refine((n) => n >= 0 && n <= max, {
      message: `must be at least 0 and at most ${max} ${unit}`
    })
    .nullable();

/**
 * The calendar year itself — the one term whose lower bound is EXCLUSIVE.
 *
 * Its own CHECK is `> 0` where the other three are `>= 0`, so it cannot share
 * the helper above. See the field's comment for what a shared `>= 0` costs.
 */
const calendarWeeksTerm = z
  .number()
  .refine((n) => n > 0 && n <= 53, {
    message: "must be greater than 0 and at most 53 weeks"
  })
  .nullable();

/**
 * The four inputs a Role's working year is built from.
 *
 * This is the authoring surface BENEATH `RoleWorkload`'s
 * `workingWeeksPerYearOverride`, not a replacement for it: the derived figure
 * stays what the coverage engine reads, and this records what it was derived
 * FROM so "why 40.6 weeks" has an answer and each term is separately arguable.
 *
 * All four required-and-nullable. `null` is "not stated" and is a named
 * read-time answer — 52 / 5 / 11 / 6 is a French statutory year, and defaulting
 * to it would misprice every other jurisdiction while looking like a filled-in
 * form.
 */
export const RoleWorkingYearInputSchema = z.strictObject({
  /**
   * Where every contract starts, in weeks. Typically 52.
   *
   * 🚨 THE LOWER BOUND IS EXCLUSIVE HERE AND INCLUSIVE ON THE OTHER THREE, and
   * that asymmetry is the database's, not a slip. `RoleWorkingYear_calendarWeeks_sane`
   * is `> 0`; the other three CHECKs are `>= 0`. A shared `>= 0` helper reads as
   * tidy and lets `calendarWeeks: 0` through to Postgres, where the CHECK fires
   * as a `P2010` and renders as a **500** — the server blamed for input this
   * boundary exists to refuse. A year of zero weeks is also the denominator of
   * every figure derived from it.
   */
  calendarWeeks: calendarWeeksTerm,
  /** Statutory and contractual paid leave, in weeks. Zero is legal. */
  paidLeaveWeeks: workingYearTerm(53, "weeks"),
  /** Worked days lost to public holidays in a normal year. Zero is legal. */
  publicHolidayDays: workingYearTerm(365, "days"),
  /** Sickness and training, in days, as a rolling average. Zero is legal. */
  sicknessDays: workingYearTerm(365, "days")
});

/**
 * A Role's stored working year, as the API returns it.
 *
 * `weeksWorked` is DERIVED and never stored — `deriveRoleWorkingYearWeeks` over
 * the four terms above, so the figure and the terms it came from cannot drift
 * apart the way two stored answers can. It is on the response because the
 * arithmetic used to exist only in a browser: a client that is not the
 * dashboard could read all four terms and had no way to reach the number the
 * dashboard drew from them.
 *
 * `null` means this Role has not stated its calendar year, which is the one
 * term the other three are subtractions from. It is NOT an error and it is not
 * a zero — see `role-working-year.derive.ts` for why a floored figure over four
 * blanks would be a number invented out of nothing.
 */
export const RoleWorkingYearViewSchema = RoleWorkingYearInputSchema.extend({
  id: z.string(),
  roleId: z.string(),
  /** Weeks actually worked, derived from the four terms. `null` until the calendar year is stated. */
  weeksWorked: z.number().nullable(),
  updatedAt: z.string()
});

/**
 * How systems inside one Role get proposed, reviewed, started and pushed.
 *
 * All five stated on every write. The columns carry defaults, and the defaults
 * exist so a sixth switch is addable without a backfill — not so a caller can
 * leave a policy unstated.
 */
export const RoleSystemPolicyInputSchema = z.strictObject({
  /** Anyone in the Role may propose a system. */
  allowProposals: z.boolean(),
  /** A system cannot go live until someone other than its author approves it. */
  requireReview: z.boolean(),
  /** New systems are built and reviewed, and only run once someone says so. */
  startPaused: z.boolean(),
  /** Changes push automatically instead of waiting in review. */
  autoPush: z.boolean(),
  /** Tell a person the first time a system handles something they used to do. */
  notifyTakeover: z.boolean()
});

/** A Role's stored system policy, as the API returns it. */
export const RoleSystemPolicyViewSchema = RoleSystemPolicyInputSchema.extend({
  id: z.string(),
  roleId: z.string(),
  updatedAt: z.string()
});

/** One job type, on the way in. */
export type RoleJobTypeInput = z.infer<typeof RoleJobTypeInputSchema>;

/** One job type, as the API returns it. */
export type RoleJobTypeView = z.infer<typeof RoleJobTypeViewSchema>;

/** The organisation's job-type library, plus the ids it could not read. */
export type RoleJobTypeLibraryView = z.infer<typeof RoleJobTypeLibraryViewSchema>;

/** One line of a Role's Scope, on the way in. */
export type RoleScopeLineInput = z.infer<typeof RoleScopeLineInputSchema>;

/** A Role's whole Scope, on the way in. */
export type RoleScopeLinesInput = z.infer<typeof RoleScopeLinesInputSchema>;

/** One stored line of a Role's Scope. */
export type RoleScopeLineView = z.infer<typeof RoleScopeLineViewSchema>;

/** A Role's whole Scope, in read order. */
export type RoleScopeLinesView = z.infer<typeof RoleScopeLinesViewSchema>;

/** One shared assumption, on the way in. */
export type RoleVariableInput = z.infer<typeof RoleVariableInputSchema>;

/** Every shared assumption of one Role, on the way in. */
export type RoleVariablesInput = z.infer<typeof RoleVariablesInputSchema>;

/** One stored assumption. */
export type RoleVariableView = z.infer<typeof RoleVariableViewSchema>;

/** Every stored assumption of one Role, in read order. */
export type RoleVariablesView = z.infer<typeof RoleVariablesViewSchema>;

/** The four inputs a Role's working year is built from, on the way in. */
export type RoleWorkingYearInput = z.infer<typeof RoleWorkingYearInputSchema>;

/** A Role's stored working year. */
export type RoleWorkingYearView = z.infer<typeof RoleWorkingYearViewSchema>;

/** A Role's system policy, on the way in. */
export type RoleSystemPolicyInput = z.infer<typeof RoleSystemPolicyInputSchema>;

/** A Role's stored system policy. */
export type RoleSystemPolicyView = z.infer<typeof RoleSystemPolicyViewSchema>;
